import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Progress } from '../../../CommonComponents/OPL/Progress/Progress';
import { Box, BoxProps } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { useTheme } from '../../../theme/theme';
import { ColumnWidths, UploadProgressRef } from '../../DocumentVault.meta';
import { ProgressIcon } from './UploadProgress.styles';

export const UploadProgress = forwardRef<UploadProgressRef, BoxProps>((props, ref) => {
  const progressValueRef = useRef<HTMLDivElement>(null);
  const progressLabelRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressIcon = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const removeProgressIconInteractivity = () => {
    if (progressIcon.current) {
      progressIcon.current.onclick = null;
      progressIcon.current.onkeydown = null;
      progressIcon.current.tabIndex = -1;
    }
  };

  useImperativeHandle(ref, () => ({
    bindOnCancel(source) {
      if (progressIcon.current) {
        const cancel = () => {
          source.cancel('Upload canceled by the user.');
        };

        progressIcon.current.onclick = () => {
          cancel();
        };

        progressIcon.current.onkeydown = evt => {
          if (evt.key === ' ' || evt.key === 'Enter') {
            cancel();
          }
        };
      }
    },

    onUploadProgress(p: any) {
      const progress = Math.ceil((p.loaded / p.total) * 100);

      if (progressValueRef.current && progressBarRef.current) {
        progressValueRef.current.innerText = `${progress}%`;
        progressBarRef.current.style.width = `${progress}%`;
      }
    },

    onUploadSuccess() {
      if (
        progressLabelRef.current &&
        progressBarRef.current &&
        progressValueRef.current &&
        progressIcon.current
      ) {
        progressLabelRef.current.innerText = 'Completed';
        progressValueRef.current.innerText = '100%';
        progressBarRef.current.style.width = '100%';
        progressBarRef.current.style.background = theme.colors.success['500'];
        progressIcon.current.className = 'fas fa-check-circle';
        progressIcon.current.style.color = theme.colors.success['500'];
        removeProgressIconInteractivity();
      }
    },

    onCancel() {
      if (
        progressLabelRef.current &&
        progressBarRef.current &&
        progressValueRef.current &&
        progressIcon.current
      ) {
        progressLabelRef.current.innerText = 'Upload Cancelled';
        progressValueRef.current.innerText = '';
        progressBarRef.current.style.width = '100%';
        progressBarRef.current.style.background = theme.colors.neutral['400'];
        progressIcon.current.style.display = 'none';
      }
    },

    onError() {
      if (
        progressLabelRef.current &&
        progressBarRef.current &&
        progressValueRef.current &&
        progressIcon.current
      ) {
        progressLabelRef.current.innerText = 'Upload Error';
        progressValueRef.current.innerText = '';
        progressBarRef.current.style.width = '100%';
        progressBarRef.current.style.background = theme.colors.error['500'];
        progressIcon.current.className = 'fas fa-times-octagon';
        progressIcon.current.style.color = theme.colors.error['500'];
        removeProgressIconInteractivity();
      }
    },
  }));

  return (
    <>
      <Box flex={ColumnWidths.LastModified + ColumnWidths.FileSize}>
        <Progress value={0} ref={progressBarRef} />

        <Box display="flex" justifyContent="space-between" mt="8">
          <Text ref={progressLabelRef} fontWeight={500} fontSize="small">
            Uploading...
          </Text>

          <Text ref={progressValueRef} fontWeight={500} fontSize="small">
            0%
          </Text>
        </Box>
      </Box>

      <Box flex={ColumnWidths.Actions} display="flex" justifyContent="center">
        <ProgressIcon
          ref={progressIcon}
          className="far fa-times-circle"
          fontSize="h6"
          color="neutral.500"
          tabIndex={0}
          role="button"
        />
      </Box>
    </>
  );
});
